import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import useSEO from '../hooks/useSEO';

const { FiCalendar, FiUser, FiArrowRight, FiX, FiTag, FiClock, FiShare2, FiCheckCircle, FiPhone } = FiIcons;

const Blog = () => {
  // Apply Dynamic SEO
  useSEO({
    title: 'CARB Compliance Blog & Clean Truck Check News | Ramirez Mobile',
    description: 'Read the latest updates on California CARB compliance, HD I/M Clean Truck Checks, smoke opacity testing, and diesel fleet maintenance.',
    canonicalRoute: '/blog'
  });

  const [selectedPost, setSelectedPost] = useState(null);

  const blogPosts = [
    {
      id: 1,
      title: "The 2024 Guide to CARB Clean Truck Checks (HD I/M)",
      excerpt: "Everything fleet managers need to know about California's transition to the new Heavy-Duty Inspection and Maintenance program.",
      category: "CARB Compliance",
      date: "March 15, 2024",
      author: "Oscar Ramirez",
      readTime: "5 min read",
      image: "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=1920&q=80",
      content: `
        <h3>The Shift to the Clean Truck Check Program</h3>
        <p>California's Air Resources Board (CARB) has officially rolled out the Heavy-Duty Inspection and Maintenance (HD I/M) program, now commonly known as the <strong>Clean Truck Check</strong>. If you operate non-gasoline commercial vehicles with a GVWR over 14,000 lbs in California, this directly impacts your bottom line.</p>
        
        <h3>Key Deadlines and Requirements</h3>
        <p>Gone are the days of the old PSIP regulations. Under the new Clean Truck Check program, fleet owners must adhere to strict periodic testing requirements:</p>
        <ul>
          <li><strong>Registration:</strong> All applicable vehicles must be registered in the CTC-VIS (Clean Truck Check - Vehicle Inspection System) database.</li>
          <li><strong>Annual Compliance Fee:</strong> A $30 per-vehicle compliance fee is required annually.</li>
          <li><strong>Periodic Testing:</strong> Starting in 2024, most commercial diesel vehicles must undergo periodic emissions testing <strong>twice per year</strong>. (Agricultural vehicles and motorhomes require annual testing).</li>
        </ul>

        <h3>How Testing Works</h3>
        <p>Testing must be performed by a CARB-credentialed tester. At Ramirez Mobile Clean Truck, we utilize certified OBD (On-Board Diagnostics) scanning tools to read your engine's emissions data directly from the ECM. If your truck is older and doesn't support OBD, a traditional SAE J1667 Smoke Opacity Test and visual inspection are required.</p>

        <h3>Avoiding DMV Holds</h3>
        <p>The most critical aspect of the Clean Truck Check is enforcement. CARB has integrated with the California DMV. If your vehicle fails to pass its periodic testing within the 90-day window before its registration expires, a <strong>DMV Registration Hold</strong> will be placed on the vehicle. You will not be able to legally operate it until it passes.</p>
        
        <div class="bg-blue-900/20 p-6 rounded-lg border border-blue-800 mt-6 mb-6">
          <h4 class="text-blue-400 font-bold mb-2">Pro Tip from Ramirez Mobile:</h4>
          <p class="text-gray-300">Don't wait until the last week of your registration window. Schedule a mobile technician to come to your yard 45-60 days early. This gives you ample time to schedule a DPF force regen or sensor replacement if your truck triggers an engine code during the test.</p>
        </div>
      `
    },
    {
      id: 2,
      title: "Demystifying Smoke Opacity Testing (SAE J1667)",
      excerpt: "What exactly is an opacity test, how does snap-acceleration work, and what are the legal smoke limits for diesel trucks in California?",
      category: "Emissions Testing",
      date: "February 28, 2024",
      author: "Oscar Ramirez",
      readTime: "4 min read",
      image: "https://images.unsplash.com/photo-1519003722824-194d4455a60c?auto=format&fit=crop&w=1920&q=80",
      content: `
        <h3>What is Smoke Opacity?</h3>
        <p>Smoke opacity refers to the degree to which exhaust smoke obscures light. In simpler terms, it's a measurement of how "thick" or "black" the soot coming out of your exhaust stack is. High opacity means high particulate matter (PM) emissions, which CARB strictly regulates.</p>
        
        <h3>The SAE J1667 Snap-Acceleration Test</h3>
        <p>The standard procedure used by certified technicians is the <strong>SAE J1667 Snap-Acceleration test</strong>. Here is how Ramirez Mobile Clean Truck performs this test at your yard:</p>
        <ol>
          <li><strong>Preparation:</strong> The engine is warmed to standard operating temperature.</li>
          <li><strong>Sensor Placement:</strong> A highly calibrated smoke meter sensor is attached to the exhaust stack.</li>
          <li><strong>The Snap:</strong> The technician rapidly depresses the accelerator pedal to the floor (the "snap") and holds it until the engine reaches maximum governed RPM, then releases it.</li>
          <li><strong>Measurement:</strong> This snap is repeated multiple times. The smoke meter calculates the average opacity percentage of the exhaust puffs.</li>
        </ol>

        <h3>What are the Passing Limits?</h3>
        <p>California's limits are strict. For most modern diesel engines equipped with a Diesel Particulate Filter (DPF) (typically 2007 and newer engine model years), the maximum allowable opacity limit is an incredibly strict <strong>5%</strong>.</p>
        <p>If your truck blows higher than 5%, it fails the Clean Truck Check. A failure almost always points to a compromised DPF (cracked substrate), a heavily face-plugged filter, or severe engine fuel-delivery issues.</p>

        <h3>What to do if you fail</h3>
        <p>If your opacity is over the limit, do not panic. Call our diagnostics team. Often, a professional forced regeneration or a DPF cleaning service can restore the filter's efficiency, allowing the truck to pass its re-test and receive its CARB compliance certificate.</p>
      `
    },
    {
      id: 3,
      title: "Why Mobile Fleet Maintenance Saves You Thousands",
      excerpt: "Taking trucks to a shop costs time and money. Learn how bringing CARB compliance testing directly to your yard increases your bottom line.",
      category: "Fleet Management",
      date: "January 12, 2024",
      author: "Oscar Ramirez",
      readTime: "3 min read",
      image: "https://images.unsplash.com/photo-1580674285054-bed31e145f59?auto=format&fit=crop&w=1920&q=80",
      content: `
        <h3>The Hidden Costs of Shop Visits</h3>
        <p>When a commercial diesel truck needs its annual DOT inspection or its semi-annual CARB Clean Truck Check, most managers default to scheduling a shop visit. But have you calculated the true cost?</p>
        <ul>
          <li><strong>Driver Pay:</strong> Paying a driver hourly to sit in a waiting room.</li>
          <li><strong>Fuel Costs:</strong> Driving an empty rig across San Bernardino or Riverside traffic to reach the service center.</li>
          <li><strong>Opportunity Cost:</strong> A truck at the shop is a truck that isn't pulling freight and generating revenue.</li>
        </ul>

        <h3>The Mobile Advantage</h3>
        <p>Ramirez Mobile Clean Truck was built to eliminate these hidden costs. By dispatching certified technicians directly to your distribution center, yard, or parking lot, we test your vehicles while they are already off-duty.</p>
        <p>If you have a fleet of 5 trucks, a mobile technician can knock out all 5 Clean Truck Checks in a single morning. If you took those 5 trucks to a dealer, you would lose hundreds of dollars in fuel and man-hours, plus days of downtime.</p>

        <h3>Comprehensive On-Site Solutions</h3>
        <p>Mobile service isn't just for checking boxes. Our service trucks are equipped with dealer-level computer diagnostic laptops. If a truck throws a check engine light or requires a DPF Force Regen, we can handle the diagnostics and the forced regeneration right where the truck is parked.</p>
        <p>Stay compliant, keep your drivers driving, and let the compliance experts come to you.</p>
      `
    }
  ];

  // Inject Blog schema for AI & Google Rich Snippets
  useEffect(() => {
    const scriptId = 'blog-schema-json-ld';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      
      const blogPostingSchema = blogPosts.map(post => ({
        "@type": "BlogPosting",
        "headline": post.title,
        "image": post.image,
        "author": {
          "@type": "Person",
          "name": post.author
        },
        "publisher": {
          "@type": "Organization",
          "name": "Ramirez Mobile Clean Truck",
          "logo": {
            "@type": "ImageObject",
            "url": "https://media-manager-c.questera.ai/greta-media/0fbf60f8fa242397e9ef833782db8ed93045df54e3b36d72437cb9900e44cd21f118b78650ffb5dd82aaaddf0850c39a/images/aW1hZ2UvanBlZw==/f2db29be14ca2eb40f068da8d183a853.jpeg"
          }
        },
        "datePublished": new Date(post.date).toISOString(),
        "description": post.excerpt
      }));

      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Blog",
            "@id": "https://ramirezmobilecleantruck.com/blog/#blog",
            "name": "Ramirez Mobile Clean Truck Blog",
            "description": "Expert articles on CARB compliance, HD I/M testing, and diesel emissions.",
            "blogPost": blogPostingSchema
          }
        ]
      });
      document.head.appendChild(script);
    }
    
    // Prevent scrolling when modal is open
    if (selectedPost) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
      const script = document.getElementById(scriptId);
      if (script) document.head.removeChild(script);
    };
  }, [selectedPost]);

  return (
    <div className="bg-gray-900 text-white min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-blue-900 py-24 overflow-hidden border-b border-gray-800">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1519003722824-194d4455a60c?auto=format&fit=crop&w=1920&q=80" 
            alt="Trucks on highway" 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/90 to-blue-900/80"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="inline-block p-3 rounded-full bg-blue-500/20 mb-6 backdrop-blur-sm border border-blue-500/30 text-blue-400 font-bold tracking-widest text-sm px-6 uppercase"
            >
              Industry Insights
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-6xl font-bold mb-6"
            >
              The CARB Compliance Blog
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed"
            >
              Stay ahead of California's strict diesel emissions regulations. Learn about Clean Truck Checks, opacity testing, and fleet maintenance.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Blog Grid Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <motion.article 
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onClick={() => setSelectedPost(post)}
                className="bg-gray-800 rounded-2xl overflow-hidden border border-gray-700 hover:border-blue-500 transition-all cursor-pointer group flex flex-col h-full shadow-lg"
              >
                {/* Article Image */}
                <div className="relative h-48 overflow-hidden">
                  <div className="absolute inset-0 bg-gray-900/20 group-hover:bg-transparent transition-colors z-10"></div>
                  <img 
                    src={post.image} 
                    alt={post.title} 
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 z-20">
                    <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                      {post.category}
                    </span>
                  </div>
                </div>

                {/* Article Content */}
                <div className="p-6 flex flex-col flex-grow">
                  <div className="flex items-center gap-4 text-xs text-gray-400 mb-4">
                    <span className="flex items-center gap-1">
                      <SafeIcon icon={FiCalendar} /> {post.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <SafeIcon icon={FiClock} /> {post.readTime}
                    </span>
                  </div>
                  
                  <h2 className="text-xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors leading-tight">
                    {post.title}
                  </h2>
                  
                  <p className="text-gray-400 text-sm mb-6 flex-grow leading-relaxed">
                    {post.excerpt}
                  </p>
                  
                  <div className="border-t border-gray-700 pt-4 flex items-center justify-between mt-auto">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-900/50 flex items-center justify-center text-blue-400 font-bold text-xs border border-blue-800">
                        {post.author.charAt(0)}
                      </div>
                      <span className="text-sm font-medium text-gray-300">{post.author}</span>
                    </div>
                    <span className="text-blue-400 font-medium text-sm flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                      Read <SafeIcon icon={FiArrowRight} />
                    </span>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Full-Screen Article Modal Reader */}
      <AnimatePresence>
        {selectedPost && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-black/80 backdrop-blur-sm"
            onClick={() => setSelectedPost(null)}
          >
            <motion.div 
              initial={{ y: 50, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-gray-900 w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl border border-gray-700 flex flex-col relative custom-scrollbar"
              onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
            >
              {/* Close Button (Sticky) */}
              <button 
                onClick={() => setSelectedPost(null)}
                className="absolute top-4 right-4 z-50 bg-gray-900/80 backdrop-blur text-gray-400 hover:text-white p-2 rounded-full border border-gray-700 hover:bg-gray-800 transition-colors shadow-lg"
                aria-label="Close article"
              >
                <SafeIcon icon={FiX} className="text-xl" />
              </button>

              {/* Modal Hero Image */}
              <div className="relative h-64 md:h-80 shrink-0">
                <img 
                  src={selectedPost.image} 
                  alt={selectedPost.title} 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent"></div>
                <div className="absolute bottom-0 left-0 p-6 md:p-10 w-full">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                      {selectedPost.category}
                    </span>
                    <span className="text-gray-300 text-sm flex items-center gap-1">
                      <SafeIcon icon={FiCalendar} /> {selectedPost.date}
                    </span>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                    {selectedPost.title}
                  </h2>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 md:p-10 bg-gray-900">
                <div className="flex items-center justify-between pb-6 mb-8 border-b border-gray-800">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-blue-900/50">
                      {selectedPost.author.charAt(0)}
                    </div>
                    <div>
                      <p className="font-bold text-white text-sm">{selectedPost.author}</p>
                      <p className="text-xs text-gray-400">CARB Compliance Specialist</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="text-gray-400 hover:text-blue-400 transition-colors p-2 bg-gray-800 rounded-full border border-gray-700" title="Share Article">
                      <SafeIcon icon={FiShare2} />
                    </button>
                  </div>
                </div>

                {/* HTML Rendered Content inside prose styling */}
                <div 
                  className="prose prose-invert prose-blue max-w-none text-gray-300 leading-relaxed font-sans
                    prose-h3:text-2xl prose-h3:font-bold prose-h3:text-white prose-h3:mt-8 prose-h3:mb-4 prose-h3:border-b prose-h3:border-gray-800 prose-h3:pb-2
                    prose-p:mb-6 prose-p:text-lg
                    prose-li:text-lg prose-ul:mb-6 prose-ol:mb-6 prose-li:mb-2
                    prose-strong:text-white"
                  dangerouslySetInnerHTML={{ __html: selectedPost.content }}
                />

                {/* Article Footer CTA */}
                <div className="mt-12 p-8 bg-blue-900/20 border border-blue-800 rounded-xl text-center">
                  <h4 className="text-2xl font-bold text-white mb-3">Need CARB Compliance Testing?</h4>
                  <p className="text-gray-300 mb-6">Ramirez Mobile Clean Truck comes directly to your yard in the Inland Empire to keep your fleet legal and on the road.</p>
                  <a 
                    href="tel:9092842738" 
                    className="inline-flex items-center gap-2 bg-blue-600 text-white font-bold px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors shadow-lg"
                  >
                    <SafeIcon icon={FiPhone} /> Call (909) 284-2738
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default Blog;